import React from 'react';
import { createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import { MembershipItem, MembershipItemType, MEMBERSHIP_ITEM_TYPES } from '@spike/membership-model';
import { DiscountField, DiscountType } from 'components/Memberships/Add/Steps/Items/Table/DiscountField';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons';

interface MembershipDiscountedItemsTableItemProps {
    item: MembershipItem;
    className?: string;
    onDelete?: (uuid: string) => void;
    onChange?: (item: MembershipItem) => void;
    onEdit?: () => void;
    currentItems: MembershipItem[];
    excludedItemIDs: string[];
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
            padding: '24px 0px',
            borderBottom: '1px solid #D4D4D4',

            [theme.breakpoints.up('md')]: {
                padding: '16px 0px'
            }
        },
        container: {
            width: '100%',
            alignItems: 'center'
        },
        itemName: {
            fontSize: 16,
            lineHeight: 1,
            fontWeight: 600,

            [theme.breakpoints.up('md')]: {
                fontWeight: 500
            }
        },
        itemType: {
            fontSize: 14,
            lineHeight: 1,
            marginTop: 4,
            color: '#7A7A7A'
        },
        itemNameColumn: {
            order: 1,

            [theme.breakpoints.down('sm')]: {
                gridColumn: 'span 2'
            }
        },
        qtyColumn: {
            order: 2,
            textAlign: 'center',

            [theme.breakpoints.down('sm')]: {
                order: 3,
                textAlign: 'left',
                gridColumn: 'span 3'
            }
        },
        qtyLabel: {
            fontSize: 14,
            lineHeight: 1,
            fontWeight: 500,
            marginBottom: 8,

            [theme.breakpoints.up('md')]: {
                display: 'none'
            }
        },
        qtyValue: {
            fontSize: 15,
            lineHeight: 1,
            fontWeight: 400,
            color: '#000000'
        },
        discountColumn: {
            order: 3,
            position: 'relative',

            [theme.breakpoints.down('sm')]: {
                order: 4,
                gridColumn: 'span 1'
            }
        },
        discountLabel: {
            fontSize: 14,
            lineHeight: 1,
            fontWeight: 500,
            marginBottom: 8,

            [theme.breakpoints.up('md')]: {
                display: 'none'
            }
        },
        discountError: {
            fontSize: 12,
            lineHeight: 1.3,
            fontWeight: 400,
            color: '#EF4F57',
            marginTop: 6
        },
        // ─── Actions column (Edit) — visible on both mobile and desktop ──────────
        actionsColumn: {
            order: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gridColumn: 'span 1',
            gap: 8,

            [theme.breakpoints.up('md')]: {
                order: 4,
                gridColumn: 'auto'
            }
        },
        editButton: {
            'fontFamily': 'Poppins',
            'fontSize': 14,
            'lineHeight': 1,
            'fontWeight': 500,
            'color': '#000000',
            'backgroundColor': 'transparent',
            'border': 'none',
            'cursor': 'pointer',
            'padding': 0,
            'textDecoration': 'underline',

            '&:hover': {
                color: '#222'
            }
        },
        deleteIconButton: {
            display: 'none'
        }
    })
);

export const MembershipDiscountedItemsTableItem: React.FC<MembershipDiscountedItemsTableItemProps> = props => {
    const classes = useStyles(props);

    const servicesState = useSelector((state: RootState) => state.services);

    const [originalPrice, setOriginalPrice] = React.useState<number | null>(null);

    React.useEffect(() => {
        if (props.item.itemID && originalPrice === null) {
            setOriginalPrice(props.item.amount);
        }
    }, [props.item.itemID, props.item.amount, originalPrice]);

    const discountType: DiscountType = React.useMemo(() => {
        if (props.item.discountPercentage !== null && props.item.discountPercentage !== undefined) {
            return 'percentage';
        }
        return 'amount';
    }, [props.item.discountPercentage]);

    const currentDiscountValue = React.useMemo(() => {
        const raw = discountType === 'amount' ? props.item.discountAmount || 0 : props.item.discountPercentage || 0;
        return typeof raw === 'string' ? parseFloat(raw) : raw;
    }, [discountType, props.item.discountAmount, props.item.discountPercentage]);

    const hasMissingDiscount: boolean = React.useMemo(() => {
        return (
            !!props.item.itemID && (props.item.discountAmount || 0) === 0 && (props.item.discountPercentage || 0) === 0
        );
    }, [props.item.itemID, props.item.discountAmount, props.item.discountPercentage]);

    const itemTypeLabel = React.useMemo(() => {
        switch (props.item.itemType) {
            case MEMBERSHIP_ITEM_TYPES.GROOMING_SERVICE:
                return 'Grooming Service';
            case MEMBERSHIP_ITEM_TYPES.DAYCARE_SERVICE:
                return 'Daycare Service';
            case MEMBERSHIP_ITEM_TYPES.BOARDING_SERVICE:
                return 'Boarding Service';
            case MEMBERSHIP_ITEM_TYPES.WALKING_SERVICE:
                return 'Walking Service';
            case MEMBERSHIP_ITEM_TYPES.TRANSPORTATION_SERVICE:
                return 'Transportation Service';
            case MEMBERSHIP_ITEM_TYPES.TRAINING_SERVICE:
                return 'Training Service';
            case MEMBERSHIP_ITEM_TYPES.PRODUCT:
                return 'Product';
            case MEMBERSHIP_ITEM_TYPES.ADD_ON:
                return 'Add-On';
            case MembershipItemType.SERVICE:
            case 'Primary Service':
                return 'Service';
            default:
                return 'Service';
        }
    }, [props.item.itemType]);

    const maxDiscountAmount = React.useMemo(() => {
        const base = originalPrice !== null ? originalPrice : props.item.amount;
        return typeof base === 'string' ? parseFloat(base) : base;
    }, [originalPrice, props.item.amount]);

    const handleDiscountChange = (value: number, type: DiscountType) => {
        props.onChange?.({
            ...props.item,
            discountAmount: type === 'amount' ? value : null,
            discountPercentage: type === 'percentage' ? value : null
        });
    };

    return (
        <div className={classes.root}>
            <div className={clsx(classes.container, props.className)}>
                <div className={classes.itemNameColumn}>
                    <Typography className={classes.itemName}>{props.item.itemName}</Typography>
                    <Typography className={classes.itemType}>{itemTypeLabel}</Typography>
                </div>

                <div className={classes.qtyColumn}>
                    <Typography className={classes.qtyLabel}>Qty</Typography>
                    <Typography className={classes.qtyValue}>Unlimited</Typography>
                </div>

                <div className={classes.discountColumn}>
                    <Typography className={classes.discountLabel}>Discount</Typography>
                    <DiscountField
                        value={currentDiscountValue}
                        discountType={discountType}
                        maxAmount={maxDiscountAmount}
                        onChange={handleDiscountChange}
                        hasError={hasMissingDiscount}
                    />
                    {hasMissingDiscount && (
                        <Typography
                            style={{ display: 'none' }}
                            className={classes.discountError}
                        >
                            Discount required
                        </Typography>
                    )}
                </div>

                {/* Desktop: Actions column (Edit + Delete) */}
                <div className={classes.actionsColumn}>
                    <button
                        type="button"
                        className={classes.editButton}
                        onClick={() => props.onEdit?.()}
                    >
                        Edit
                    </button>
                    <button
                        type="button"
                        className={classes.deleteIconButton}
                        onClick={() => props.onDelete?.(props.item.uuid || '')}
                    >
                        <FontAwesomeIcon icon={faTrashAlt} />
                    </button>
                </div>
            </div>
        </div>
    );
};
